
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart3, PieChart, TrendingUp, DollarSign } from "lucide-react";

// Dashboard icons mapping to make cards more visually interesting
const iconMapping = {
  "Financial": <DollarSign className="h-5 w-5" />,
  "Performance": <TrendingUp className="h-5 w-5" />,
  "Occupancy": <BarChart3 className="h-5 w-5" />,
  "Revenue": <PieChart className="h-5 w-5" />,
  // Default icon if no match is found
  "default": <BarChart3 className="h-5 w-5" />
};

// Helper function to choose an icon based on dashboard name
const getDashboardIcon = (dashboardName: string) => {
  // Check if the dashboard name contains any of the keys
  for (const [key, icon] of Object.entries(iconMapping)) {
    if (dashboardName.toLowerCase().includes(key.toLowerCase())) {
      return icon;
    }
  }
  // Return default icon if no match
  return iconMapping.default;
};

const Dashboard = () => {
  const { user } = useAuth();
  const [dashboards, setDashboards] = useState<any[]>([]);

  useEffect(() => {
    // Get dashboards from sessionStorage
    const storedDashboards = sessionStorage.getItem("dashboards");
    if (storedDashboards) {
      try {
        const parsedDashboards = JSON.parse(storedDashboards);
        setDashboards(parsedDashboards);
      } catch (error) {
        console.error("Error parsing dashboards from sessionStorage:", error);
        setDashboards([]);
      }
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="pt-0 pb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="sm:flex sm:items-center sm:justify-between py-6">
            <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
            <p className="mt-2 text-sm text-gray-700 sm:mt-0">
              Welcome back, <span className="font-medium">{user?.username}</span>
            </p>
          </div>
        </div>
      </div>

      <main className="pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Dashboard Cards Section */}
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Available Dashboards</h2>
          {dashboards.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {dashboards.map((dashboard) => (
                <Link key={dashboard.id} to={`/dashboard/analytics/${dashboard.id}`} className="block">
                  <Card className="h-full hover:shadow-md transition-shadow">
                    <CardHeader className="pb-2 flex flex-row items-center justify-between">
                      <div>
                        <CardTitle>{dashboard.name}</CardTitle>
                        <CardDescription>
                          {dashboard.description || "Analytics dashboard"}
                        </CardDescription>
                      </div>
                      <div className="rounded-full bg-secondary p-2 text-primary">
                        {getDashboardIcon(dashboard.name)}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-500">
                        Click to view detailed analytics
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <p className="text-gray-500">No dashboards available. Check back later.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
